import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPagesByParent,
    PageLinkModel,
    PagesCollectionModel,
} from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { LinkListStyles } from "@insite/content-library/Widgets/Basic/LinkList";
import Link from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import breakpoint from "@store/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface BlogListLinksStyles {
    blogListLinksWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkListWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    link?: LinkListStyles;
    selectWrapper?: InjectableCss;
    select?: SelectPresentationProps;
}

const mapStateToProps = (state: ApplicationState) => {
    const blogHomeId = state.links.pageTypesToNodeId.BlogHomePage;
    return { blogHomeId, state };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export const blogListLinksStyles: BlogListLinksStyles = {
    blogListLinksWrapper: {
        css: css`
            text-transform: uppercase;
            padding: ${pxToRem(10)} 0;
        `,
    },
    titleWrapper: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    selectWrapper: {
        css: css`
            ${breakpoint("sm", "up")} {
                display: none;
            }
        `,
    },
    linkListWrapper: {
        css: css`
            ${breakpoint("xs", "down")} {
                display: none;
            }
        `,
    },
    linkWrapper: {
        css: css`
            padding: ${pxToRem(10, 18, 0, 0)};
            text-transform: none;
        `,
    },
    title: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.large};
        `,
    },
};

const styles = blogListLinksStyles;

const BlogListLinks = ({ blogHomeId, state }: Props) => {
    const history = useHistory();
    const [pages, setPages] = useState<PagesCollectionModel>();
    const [links, setLinks] = useState<PageLinkModel[]>([]);

    useEffect(() => {
        if (blogHomeId) {
            const fetchData = async () => {
                const result = await getPagesByParent({ parentNodeId: blogHomeId });
                setPages(result);
            };
            fetchData();
        }
    }, [blogHomeId]);

    useEffect(() => {
        if (pages && pages.pages.length > 0) {
            const linkList: PageLinkModel[] = pages.pages.map(page => {
                return getPageLinkByNodeId(state, page.nodeId)!;
            });
            setLinks(linkList);
        }
    }, [pages]);

    if (!pages || !pages.pages.length || !links || !links.length) {
        return null;
    }

    const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const href = event.currentTarget.value;
        history.replace(href);
    };

    return (
        <StyledWrapper {...styles.blogListLinksWrapper}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography as="h2" {...styles.title}>
                    {translate("Category")}
                </Typography>
            </StyledWrapper>
            <StyledWrapper as="ul" {...styles.linkListWrapper}>
                {links.map((link, key) => (
                    <StyledWrapper as="li" key={key} {...styles.linkWrapper}>
                        <Link href={link.url}>{link.title}</Link>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
            <StyledWrapper {...styles.selectWrapper}>
                <Select {...styles.select} onChange={handleSelectChange}>
                    {links.map((link, key) => (
                        <option key={key} value={link.url}>
                            {link.title}
                        </option>
                    ))}
                </Select>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(BlogListLinks),
    definition: {
        group: constants.widgetGroup.blogPage,
        icon: "ax-link-list",
    },
};

export default widgetModule;
